import { queryOptions, useQuery } from '@tanstack/react-query';

import { ApiResponse, QueryRefetchInterval } from '@cast/shared-types';

import { apiClient } from '@cast/infrastructure-api';
import { overrideOrganizationHeader } from '@cast/api-client';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

const apiFn = apiClient.clusterManagement.getComponents;

const CLUSTER_MANAGEMENT_COMPONENTS_QUERY_KEY = 'cluster-management-components';

type ComponentsQueryData = ApiResponse<typeof apiFn>;

const getComponentsQueryOptions = (organizationId: string) =>
  queryOptions({
    queryKey: [
      CLUSTER_MANAGEMENT_COMPONENTS_QUERY_KEY,
      organizationId,
    ] as const,
    queryFn: async () => {
      const response = await apiFn(
        { organizationId },
        { headers: overrideOrganizationHeader(organizationId) }
      );
      return response.data;
    },
  });

type ComponentsQueryArgs<SELECT> = {
  enabled?: boolean;
  select?: (data: ComponentsQueryData) => SELECT;
  staleTime?: number;
  refetchInterval?: QueryRefetchInterval<ComponentsQueryData, SELECT>;
};

export const useComponentsQuery = <SELECT = ComponentsQueryData>({
  enabled,
  select,
  staleTime,
  refetchInterval,
}: ComponentsQueryArgs<SELECT> = {}) => {
  const { currentOrganization } = useOrganizations();

  return useQuery({
    ...getComponentsQueryOptions(currentOrganization?.id ?? ''),
    enabled,
    select,
    staleTime,
    refetchInterval,
  });
};
